"use client";
import { toast } from "components/ui/use-toast";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import sample from "lodash/sample";
import login, { getErrorMessage, setupAccessToken } from "lib/login";
import AuthenticationHandler from "components/authentication-handler";
import { performAuthenticatedCall } from "lib/utils";
import { Alert, AlertTitle, AlertDescription } from "components/ui/alert";
import { AlertCircle, Mail, Phone, RectangleEllipsis, Zap } from "lucide-react";
import { useReCaptcha } from "next-recaptcha-v3";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { Stores, Users } from "@prisma/client";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "components/ui/drawer";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "components/ui/input-otp";

function Page() {
  const { push } = useRouter();
  const email = useRef<string>("");
  const password = useRef<string>("");
  const [isPasswordlessLogin, setIsPasswordlessLogin] = useState(false);
  const [video, setVideo] = useState<string>();
  const [loginError, setLoginError] = useState<string | undefined | null>();

  const [
    isResetPasswordSuccessfullyGenerated,
    setIsResetPasswordSuccessfullyGenerated,
  ] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);

  // Import 'executeRecaptcha' using 'useReCaptcha' hook
  const { executeRecaptcha } = useReCaptcha();

  useEffect(() => {
    const getVideo = () => {
      const video = sample([
        "/5794186-uhd_2160_3840_25fps.mp4",
        "/13737109-uhd_2160_3840_24fps.mp4",
        "/17165233-uhd_2160_3840_25fps.mp4",
        "/7008563-hd_1080_1920_25fps.mp4",
        "/16624891-uhd_2160_3840_30fps.mp4",
      ]);

      return video;
    };

    setVideo(getVideo());
  }, []);

  const handleLogin = async () => {
    if (!email.current || !password.current) {
      toast({
        duration: 1000,
        description: "L'email et le mot de passe sont obligatoires",
        variant: "destructive",
      });

      return;
    }

    setLoginError(null);
    setIsLoginLoading(true);

    try {
      await login(email.current, password.current);

      const user = await performAuthenticatedCall<
        Users & { defaultStore: { slug: string } }
      >("/users/current", "", "GET");

      if ("$crisp" in window) {
        (window.$crisp as { push: (arg: Array<string>) => void }).push([
          "do",
          "chat:hide",
        ]);
      }

      if (!user.isAccountActive) {
        push(`/activate`);
        return;
      }

      if (!user.defaultStoreId) {
        push(`/stores/new`);
        return;
      }

      push(`/s/${user.defaultStore.slug}/orders`);
    } catch (error) {
      setLoginError(
        getErrorMessage((error as unknown as { code: string })?.code)
      );

      throw error;
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleChangeEmail = ({ target }: { target: { value: string } }) => {
    email.current = target.value;
  };

  const handleChangePassword = ({ target }: { target: { value: string } }) => {
    password.current = target.value;
  };

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === "Enter") {
      // Enter key was pressed
      handleLogin();
    }
  };

  const handleGenerateTextMessageOneTimePassword = async () => {
    if (
      !email.current ||
      !email.current
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    ) {
      toast({
        duration: 1000,
        description: "Email invalide",
        variant: "destructive",
      });

      return;
    }

    // Generate ReCaptcha token
    const token = await executeRecaptcha("form_submit");

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/one-time-passwords/text-message`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            email: email.current,
            token,
          }),
        }
      );

      const data = await response.json();

      if (data.statusCode) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      toast({
        duration: 1000,
        description: "On vient de t'envoyer le mot de passe à usage unique",
      });

      setIsResetPasswordSuccessfullyGenerated(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithOneTimePassword = async (oneTimePassword: string) => {
    if (
      !email.current ||
      !email.current
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ) ||
      !oneTimePassword
    ) {
      toast({
        duration: 1000,
        description: "L'email ou le mot de passe n'est pas renseigné",
        variant: "destructive",
      });

      return;
    }

    // Generate ReCaptcha token
    const token = await executeRecaptcha("form_submit");

    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ORIGIN}/one-time-passwords/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Adjust the content type as needed
            // Add any other headers if required
          },
          body: JSON.stringify({
            token,
            oneTimePassword,
            email: email.current,
          }),
        }
      );

      const data = await response.json();

      if (data.statusCode) {
        // Show error to your customer (for example, payment details incomplete)
        toast({
          duration: 1000,
          variant: "destructive",
          description: data.message,
        });

        return;
      }

      const auth = getAuth();

      const result = await signInWithCustomToken(auth, data.token);

      await setupAccessToken(result.user);

      const user = await performAuthenticatedCall<
        Users & { defaultStore: { slug: string } }
      >("/users/current", "", "GET");

      if ("$crisp" in window) {
        (window.$crisp as { push: (arg: Array<string>) => void }).push([
          "do",
          "chat:hide",
        ]);
      }

      push(`/s/${user.defaultStore.slug}/orders`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthenticationHandler>
      <div className="w-full xl:grid xl:grid-cols-2 bg-secondary">
        <div className="h-screen flex items-center justify-center">
          <div className="mx-auto grid w-[350px] gap-6">
            <div className="grid gap-2 text-center">
              <h1 className="text-3xl font-bold">{"Se connecter"}</h1>
              {!isPasswordlessLogin ? (
                <p className="text-balance text-muted-foreground">
                  {"Renseigne ton email et mot de passe pour te connecter"}
                </p>
              ) : (
                <p className="text-balance text-muted-foreground">
                  {"Renseigne ton email pour recevoir le code d'accès"}
                </p>
              )}
            </div>

            <div className="grid gap-4">
              {loginError && (
                <Alert
                  variant="destructive"
                  className="bg-destructive text-destructive-foreground border-0"
                >
                  <AlertCircle className="h-4 w-4 stroke-destructive-foreground" />
                  <AlertTitle>{"Oups !"}</AlertTitle>
                  <AlertDescription>
                    <p>{loginError}</p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid gap-2">
                <Label htmlFor="email">{"Email"}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jane.doe@example.com"
                  required
                  onChange={handleChangeEmail}
                  onKeyDown={handleKeyDown}
                />
              </div>

              {!isPasswordlessLogin ? (
                <>
                  <div className="grid gap-2">
                    <div className="flex items-center">
                      <Label htmlFor="password">{"Mot de passe"}</Label>
                      <Link
                        href="/forgot-password"
                        className="ml-auto inline-block text-sm underline"
                      >
                        {"Mot de passe oublié ?"}
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="**********"
                      required
                      onChange={handleChangePassword}
                      onKeyDown={handleKeyDown}
                    />
                  </div>

                  <Button 
                    className={`w-full transition-all duration-500 ${
                      isLoginLoading 
                        ? 'bg-gradient-to-r from-primary/80 via-primary to-primary/80 animate-pulse shadow-lg shadow-primary/50 ring-2 ring-primary/30 ring-offset-1' 
                        : ''
                    }`}
                    onClick={handleLogin} 
                    disabled={isLoginLoading}
                  >
                    {"Se connecter"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="default"
                    className="w-full space-x-2"
                    onClick={handleGenerateTextMessageOneTimePassword}
                    disabled={isLoading}
                  >
                    <Phone className="w-4 h-4" />
                    <p>{"Recevoir un code par message"}</p>
                  </Button>

                  <Button
                    variant="ghost"
                    className="w-full space-x-2"
                    onClick={() => setIsPasswordlessLogin(true)}
                  >
                    <Mail className="w-4 h-4" />
                    <p>{"Recevoir un code par mail"}</p>
                  </Button>
                </>
              )}

              {!isPasswordlessLogin ? (
                <Button
                  variant="outline"
                  className="w-full space-x-2"
                  onClick={() => setIsPasswordlessLogin(true)}
                >
                  <Zap className="w-4 h-4" />
                  <p>{"Accès sans mot de passe"}</p>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full space-x-2"
                  onClick={() => setIsPasswordlessLogin(false)}
                >
                  <RectangleEllipsis className="w-4 h-4" />
                  <p>{"Accès avec mot de passe"}</p>
                </Button>
              )}
            </div>

            <div className="mt-4 text-center text-sm">
              {"Pas encore de compte ? "}
              <Link href="/sign-up" className="underline">
                {"S'inscrire"}
              </Link>
            </div>
          </div>
        </div>

        <div className="hidden xl:block relative h-screen overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="hidden xl:block absolute top-0 bottom-0 min-h-full min-w-full scale-110 object-cover -my-28"
          >
            <source src={video} type="video/mp4" />
          </video>
        </div>
      </div>

      <Drawer
        open={isResetPasswordSuccessfullyGenerated}
        onOpenChange={(isOpen) => {
          setIsResetPasswordSuccessfullyGenerated(isOpen);
        }}
      >
        <DrawerContent className="h-2/3">
          <DrawerHeader>
            <DrawerTitle>{"Saisir le mot de passe à usage unique"}</DrawerTitle>
          </DrawerHeader>
          <DrawerDescription className="space-y-2 p-4 h-full flex items-center justify-center">
            <InputOTP
              maxLength={6}
              disabled={isLoading}
              onChange={(value: string) => {
                if (value.length === 6) {
                  handleLoginWithOneTimePassword(value);
                }
              }}
              className={`${isLoading ? "animate-pulse opacity-75" : ""}`}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className={isLoading ? "animate-pulse" : ""} />
                <InputOTPSlot index={1} className={isLoading ? "animate-pulse" : ""} />
                <InputOTPSlot index={2} className={isLoading ? "animate-pulse" : ""} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} className={isLoading ? "animate-pulse" : ""} />
                <InputOTPSlot index={4} className={isLoading ? "animate-pulse" : ""} />
                <InputOTPSlot index={5} className={isLoading ? "animate-pulse" : ""} />
              </InputOTPGroup>
            </InputOTP>
          </DrawerDescription>
        </DrawerContent>
      </Drawer>
    </AuthenticationHandler>
  );
}

export default Page;
